import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Send, Sparkles, Plus, ClipboardList, Loader2,
  Brain, Clock, Trash2, ArrowLeft, AlertTriangle, ChevronRight, Info, Shield,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import FeatureGate from '../components/FeatureGate';
import { InfoTip } from '../components/ui';
import {
  aiRiskChat, aiRiskAnalyze, aiRiskHistory, aiRiskLoadConversation,
} from '../services/api';

// ─── Welcome Screen ──────────────────────────────────────────────────────────
// Sample questions are deliberately risk-management framed — never investment
// advice ("should I buy X", "is now a good time to sell"). The analyst is a
// portfolio-risk tool, not a market oracle.
const SAMPLE_QUESTIONS = [
  "Analyze my trade size anomalies over the last 30 days",
  "Which of my exchanges has the highest concentration risk?",
  "Identify my riskiest pair exposures right now",
  "Review my time-of-day trading pattern — when do I trade most?",
  "Compare my CEX vs DEX activity — am I balanced?",
  "Give me 3 specific risk-reduction improvements",
];

function WelcomeScreen({ onSampleClick }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-indigo-500/20 border border-violet-500/30 flex items-center justify-center mb-5">
        <Sparkles className="w-8 h-8 text-violet-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">Your AI Risk Analyst</h2>
      <p className="text-gray-500 text-sm text-center max-w-md mb-3">
        I analyze your trading data, explain anomalies, and surface risks. Powered by haythix AI.
      </p>

      {/* Data transparency disclosure — users should know what the AI sees */}
      <div className="max-w-lg w-full mb-6 bg-violet-500/5 border border-violet-500/15 rounded-xl px-3 py-2.5">
        <div className="flex items-start gap-2">
          <Shield className="w-3.5 h-3.5 text-violet-400 mt-0.5 flex-shrink-0" />
          <div className="text-[11px] text-violet-300/90 leading-relaxed">
            <p className="font-semibold mb-0.5 flex items-center gap-1.5">
              What I have access to
              <InfoTip side="bottom">Scope is deliberately narrow for privacy + focused reasoning. Investment-advice questions ("should I buy X") get redirected to risk analysis because the AI has no market data. Reframe as "what are the risks in my portfolio" or "walk through what could go wrong with my biggest position."</InfoTip>
            </p>
            <p className="text-violet-300/70">
              Your bot configurations · last 7 days of daily counters · last 3 days of trade records (max 50) · aggregate volume + gas spend.
              I do NOT see current market prices, news, sentiment, or anything outside this window.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
        {SAMPLE_QUESTIONS.map((q, i) => (
          <button
            key={i}
            onClick={() => onSampleClick(q)}
            className="text-left px-4 py-3 rounded-xl bg-dark-800 border border-dark-700/50 text-sm text-gray-400
              hover:border-violet-500/40 hover:text-gray-300 hover:bg-dark-700 transition-all group"
          >
            <span className="text-violet-400 mr-2 group-hover:text-violet-300">→</span>
            {q}
          </button>
        ))}
      </div>

      {/* Investment advice disclaimer */}
      <div className="max-w-lg w-full mt-6 bg-amber-500/5 border border-amber-500/15 rounded-xl px-3 py-2.5">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-300/90 leading-relaxed">
            <strong>Not investment advice.</strong> haythix AI analyzes your data to surface risks and patterns —
            it does not predict prices or recommend specific trades. All trading decisions are yours.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Chat Message ────────────────────────────────────────────────────────────

function ChatMessage({ role, content }) {
  const isUser = role === 'user';

  return (
    <div className={`flex gap-3 px-4 py-4 ${isUser ? '' : 'bg-dark-800/40'}`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-xs font-bold ${
        isUser
          ? 'bg-accent-primary/15 text-accent-primary'
          : 'bg-violet-500/15 text-violet-400'
      }`}>
        {isUser ? 'You' : <Sparkles className="w-4 h-4" />}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 text-sm leading-relaxed">
        {isUser ? (
          <p className="text-gray-200">{content}</p>
        ) : (
          <div className="prose prose-invert prose-sm max-w-none
            prose-headings:text-white prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
            prose-p:text-gray-300 prose-p:my-1.5
            prose-li:text-gray-300 prose-li:my-0.5
            prose-strong:text-white prose-strong:font-semibold
            prose-code:text-violet-300 prose-code:bg-dark-700 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
            prose-ul:my-2 prose-ol:my-2
            prose-hr:border-dark-600 prose-hr:my-3
          ">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Typing Indicator ────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex gap-3 px-4 py-4 bg-dark-800/40">
      <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-violet-500/15">
        <Sparkles className="w-4 h-4 text-violet-400" />
      </div>
      <div className="flex items-center gap-1.5 pt-2">
        {[0, 1, 2].map(i => (
          <span
            key={i}
            className="w-2 h-2 rounded-full bg-violet-400"
            style={{
              animation: 'pulse 1.4s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
              opacity: 0.4,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Conversation History Sidebar ────────────────────────────────────────────

function HistorySidebar({ conversations, activeId, onSelect, onNew, show, onClose }) {
  return (
    <>
      {/* Mobile backdrop */}
      {show && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        fixed lg:relative z-50 lg:z-auto
        top-0 left-0 h-full w-72 lg:w-64
        bg-dark-800 border-r border-dark-700/50
        flex flex-col flex-shrink-0
        transition-transform duration-200
        lg:translate-x-0
        ${show ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* New conversation button */}
        <div className="p-3 border-b border-dark-700/50">
          <button
            onClick={onNew}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
              bg-violet-500/15 text-violet-400 border border-violet-500/30
              hover:bg-violet-500/25 transition-all"
          >
            <Plus className="w-4 h-4" /> New Chat
          </button>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {conversations.length === 0 && (
            <p className="text-xs text-gray-600 text-center py-6">No conversations yet</p>
          )}
          {conversations.map(conv => (
            <button
              key={conv.conversationId}
              onClick={() => onSelect(conv.conversationId)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-sm transition-all ${
                activeId === conv.conversationId
                  ? 'bg-violet-500/15 text-violet-300'
                  : 'text-gray-400 hover:bg-dark-700 hover:text-gray-300'
              }`}
            >
              <MessageSquare className="w-4 h-4 flex-shrink-0 opacity-50" />
              <span className="truncate flex-1">{conv.title || 'Untitled'}</span>
              <span className="text-[10px] text-gray-600 flex-shrink-0">{conv.messageCount}</span>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-dark-700/50">
          <div className="flex items-center gap-2 text-[10px] text-gray-600">
            <Brain className="w-3.5 h-3.5" />
            <span>Powered by haythix AI</span>
          </div>
        </div>
      </aside>
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AiRiskAnalystPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  async function loadHistory() {
    try {
      const data = await aiRiskHistory();
      setConversations(data?.conversations || []);
    } catch {
      // Silently fail — sidebar will be empty
    }
  }

  async function loadConversation(convId) {
    try {
      const data = await aiRiskLoadConversation(convId);
      if (data) {
        setMessages(data.messages || []);
        setConversationId(data.conversationId);
        setShowSidebar(false);
      }
    } catch {
      setError('Failed to load conversation');
    }
  }

  function startNewConversation() {
    setMessages([]);
    setConversationId(null);
    setInput('');
    setError(null);
    setShowSidebar(false);
  }

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setInput('');
    setError(null);
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const result = await aiRiskChat(msg, conversationId);
      setMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
      if (!conversationId && result.conversationId) {
        setConversationId(result.conversationId);
        loadHistory();
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
      }]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [input, loading, conversationId]);

  async function runFullAnalysis() {
    if (loading) return;
    setError(null);
    setMessages(prev => [...prev, { role: 'user', content: 'Run a comprehensive 7-day analysis of my trading performance' }]);
    setLoading(true);

    try {
      const result = await aiRiskAnalyze(7);
      setMessages(prev => [...prev, { role: 'assistant', content: result.analysis }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I couldn\'t complete the analysis. Please try again.',
      }]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <FeatureGate feature="aiRiskAnalyst">
      {/* Typing animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>

      <div className="flex h-[calc(100vh-8rem)] -mx-4 -mt-4 lg:-mx-6 lg:-mt-6 animate-fade-in">
        {/* History sidebar */}
        <HistorySidebar
          conversations={conversations}
          activeId={conversationId}
          onSelect={loadConversation}
          onNew={startNewConversation}
          show={showSidebar}
          onClose={() => setShowSidebar(false)}
        />

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-dark-700/50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSidebar(true)}
                className="lg:hidden p-1.5 text-gray-500 hover:text-white transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-accent-primary flex items-center gap-2">
                  <Brain className="w-5 h-5" /> AI Risk Analyst
                </h1>
                <p className="text-xs text-gray-500">Chat with your trading data</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={runFullAnalysis}
                disabled={loading}
                className="px-4 py-2 rounded-xl text-xs font-semibold
                  bg-violet-500/15 text-violet-400 border border-violet-500/30
                  hover:bg-violet-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-all
                  hidden sm:flex items-center gap-1.5"
              >
                <ClipboardList className="w-3.5 h-3.5" /> Full Analysis
              </button>
              <button
                onClick={startNewConversation}
                className="p-2 rounded-xl text-gray-500 hover:text-white hover:bg-dark-700 transition-all"
                title="New conversation"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto">
            {messages.length === 0 ? (
              <WelcomeScreen onSampleClick={(q) => sendMessage(q)} />
            ) : (
              <div className="max-w-3xl mx-auto">
                {messages.map((msg, idx) => (
                  <ChatMessage key={idx} role={msg.role} content={msg.content} />
                ))}
                {loading && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Error bar */}
          {error && (
            <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
              <p className="text-xs text-red-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </p>
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-dark-700/50 p-4 flex-shrink-0">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-end gap-2 bg-dark-800 border border-dark-700/50 rounded-2xl px-4 py-3
                focus-within:border-violet-500/40 transition-colors">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about your trading data..."
                  rows={1}
                  disabled={loading}
                  className="flex-1 bg-transparent text-sm text-white placeholder-gray-600
                    resize-none outline-none min-h-[24px] max-h-[120px]
                    disabled:opacity-50"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  className="p-2 rounded-xl bg-violet-500 text-white
                    hover:bg-violet-400 disabled:opacity-30 disabled:cursor-not-allowed
                    transition-all flex-shrink-0"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-gray-700 text-center mt-2">
                AI responses are based on your trading data. Always verify recommendations independently.
              </p>
            </div>
          </div>
        </div>
      </div>
    </FeatureGate>
  );
}
